/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type ColumnDef, type Filter, type Params, CAPACITY_UNIT, I18nService, PermissionService, UNPLACEHOLDER, UtilService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { LicenseKey } from 'app/common/constants/license-key';
import { type Initiator } from 'app/common/types/mo';
import { FeatureConfig } from 'app/config/feature';

import { HostOsTypeE, MO } from '../../../../common/constants';
import { ProductConfig, SWITCH } from '../../../../config';
import { HostNoRedundancyCheckE } from './type';

@Injectable()
export class HostsStrService {
    SWITCH = SWITCH;

    /**
     * i18n冒号
     */
    colon = this.i18n.get('common.colon_label');

    i18nLabel = {
        addNewInitiatorBtn: this.get('host.host_task_add_newInitiator_btn'),
        addExistInitiatorBtn: this.get('host.host_task_add_existInitiator_btn'),
        showNoVstoreInitiatorLab: this.get('host.host_task_create_notBelongVstore_checkbox'),
        vstoreLab: this.get('common.owningVstore_lab'),
        vstoreName: this.get('vstore_name_lab'),
        vstoreId: this.get('vstore_id_lab'),
        host: this.get('common.host_title'),
        hostIpAddress: this.get('host.host_view_toplogy_pathDetails_HostIP_column'),
        hosts: this.get('common.hosts_title'),
        hostGroup: this.get('common.hostGroup_title'),
        hostGroups: this.get('common.hostGroups_title'),
        initiator: this.get('host.host_term_initiators_tab'),
        createBtn: this.get('common.create_button'),
        mappingBtn: this.get('common.map_button'),
        scanBtn: this.get('host.host_task_scanHosts_button'),
        scanBtnS: this.get('common.initializeConfig_term_scan_button'),
        deleteBtn: this.get('common.delete_button'),
        closeBtn: this.get('common.close_button'),
        addBtn: this.get('common.add_button'),
        okBtn: this.get('common.confirm_button'),
        cancelBtn: this.get('common.cancel_button'),
        removeBtn: this.get('common.remove_button'),
        modifyBtn: this.get('common.modify_button'),
        associateHostBtn: this.get('host.host_term_associateHost_button'),
        cancelAssociateHostBtn: this.get('host.host_term_cancelAssociateHost_button'),
        associateHostDisableTips: this.get('host.host_term_associateHost_error_tip'),
        cancelAssociateHostDisableTips: this.get('host.host_term_cancelAssociateHost_error_tip'),
        cancelModifyBtn: this.get('common.cancelModify_button'),
        ultrapathHostStatusTips: this.get('host.host_task_scanHosts_error_tip'),
        ultrapathHostSStatusTips: this.get('host.host_task_simplifiedMode_scanHosts_error_tip'),
        ultrapathHostTenantStatusTips: this.get('host.vstore_host_task_scanHosts_error_tip'),
        changeAluaTip: this.get('protection.protectionGroup_task_HyperMetro_changeHostAccessMode_msg'),
        changeAluaTipOem: this.get('protection.protectionGroup_task_HyperMetro_changeHostAccessMode_oem_msg'),
        changeAluaTitle: this.get('host.host_view_task_modifyHostAccessMode_info_title'),
        noRedundancyCheckLab: this.get('host.host_term_noLinkRedundancyCheck_lab'),
        noRedundancyCheckContent: this.get('host.host_task_disable_linkredundancycheck_msg'),
        yesLab: this.get('common.term_yes_label'),
        noLab: this.get('common.term_No_label'),
        iSCSILab: this.get('common.iSCSI_lab'),
        FCLab: this.get('common.FC_lab'),
        nvmeofRoceLab: this.get('common.NVMeoF_RoCE_lab'),
        hostNum: this.get('host.hostGroup_term_hostsNum_lab'),
        actionBtn: this.get('common.operation_button'),
        summary: this.get('common.task_collectInfo_title_label'),
        topology: this.get('common.topology_lab'),
        mappingLab: this.get('storage.LUN_task_view_mapping_tab'),
        mappingsLab: this.get('storage.LUN_task_view_mappings_tab'),
        memberHosts: this.get('host.hostGroup_view_memberHost_lab'),
        description: this.get('common.description_column'),
        status: this.get('common.status_column'),
        ipAddress: this.get('common.IPaddress_column'),
        owningGroup: this.get('host.host_view_summary_belongedHostGroup_label'),
        os: this.get('common.operatingSystem_lab'),
        lunUsage: this.get('host.host_view_summary_lunCapUsage_label'),
        totalCapacity: this.get('common.capacity_column'),
        deviceLocation: this.get('common.location_column'),
        initiators: this.get('host.host_view_initiator_title'),
        memberInitiators: this.get('host.host_view_initiator_title'),
        type: this.get('common.type_column'),
        iqn: this.get('common.IQN_label'),
        unknown: this.get('common.EnumDefine_RUNNING_STATUS_E_UNKNOWN'),
        online: this.get('common.EnumDefine_RUNNING_STATUS_E_ONLINE'),
        offline: this.get('common.EnumDefine_RUNNING_STATUS_E_OFFLINE'),
        initing: this.get('common.EnumDefine_RUNNING_STATUS_INITIALIZING'),
        deleting: this.get('common.EnumDefine_RUNNING_STATUS_REMOVING'),
        modifying: this.get('common.modifying_lab'),
        basicInfoLab: this.get('common.basicInfo_title'),

        createSingleHost: this.get('host.host_task_createAHost_title'),
        createHost: this.get('host.host_task_create_host_button'),
        advance: this.get('common.advance_label'),
        name: this.get('common.name_column'),
        hostName: this.get('host.host_term_HostName_column'),
        aluaTitle: this.get('host.host_task_create_configALUA_lab'),
        aluaMode: this.get('host.host_task_create_configALUA_switchingMode_lab'),
        pathType: this.get('host.host_task_create_configALUA_pathType_lab'),

        createMultiHost: this.get('host.host_task_batchCreateHosts_title'),
        quantity: this.get('common.quantity_label'),
        startNum: this.get('host.host_task_create_startNumber_lab'),

        createHostGroup: this.get('host.hostGroup_task_create_hostGroup_button'),
        createInitiator: this.get('host.hostGroup_task_create_initiator_button'),
        wwpn: this.get('common.WWPN_lab'),
        nqn: this.get('common.NQN_lab'),
        alias: this.get('common.alias_lab'),
        chap: this.get('common.CHAP_label'),
        chapName: this.get('common.chapName_lab'),
        password: this.get('common.password_label'),
        confirmPassword: this.get('common.confirmPassword_label'),
        wwpnOrIqn: this.get('common.WWNorIQN_label'),
        // 微存储直通只支持roce [NQN]
        wwpnOrIqnOrNqn: this.get(SWITCH.IS_DIRECT_MODE ? 'common.NQN_lab' : 'common.WWPNorIQNorNQN_lab'),
        wwpnLength: this.get('common.lengthEqual_Num_tip', [16]),
        normalAuth: this.get('host.host_task_create_initiator_normalAuthenticate_label'),
        discoveryAuth: this.get('host.host_task_create_initiator_discoveryAuthenticate_label'),
        noAuth: this.get('host.host_task_create_initiator_noAuthenticate_listbox'),
        unidicectional: this.get('host.host_task_create_initiator_singleDirection_listbox'),
        bidirectional: this.get('host.host_task_create_initiator_dualDirection_listbox'),

        mapping: this.get('common.map_button'),
        mapLunGroup: this.get('host.host_task_mappingLunGroup_lab'),
        mapLun: this.get('host.host_task_mappingLun_lab'),

        noInitiatorTip: this.get('common.host_task_removeInitiators_error_tips'),
        unmapBelongToLunGroupTip: this.get('common.LUN_task_unmap_belongToLUNGroup_error_tips'),
        unmapBelongToHostGroupTip: this.get('common.host_task_unmap_belongTohostGroup_error_tips'),
        modifyLunIdTip: this.get('common.host_task_modifyLUNID_belongToLUNGroup_error_tips'),
        addPortGroupTip: this.get('common.LUNGroup_task_topology_addPort_error_tips'),
        removePortGroupTip: this.get('common.LUNGroup_task_topology_removePort_error_tips'),
        removeHostTips: this.get('common.hostGroup_task_removeHost_error_tips'),
        deleteHostGroupTip: this.get('common.LUNGroup_task_delete_error_tips'),
        deleteHostTip: this.get('common.host_task_delete_error_tips'),
        unavailablePortGroupTip: this.get('common.host_task_unavailablePortGroup_error_tips'),

        mappingLunGroupTip: this.get('host.host_task_mappingLunGroup_selectLunGroup_lab'),
        new: this.get('common.newCreate_checkbox'),
        existed: this.get('common.Existed_checkbox'),
        lunGroup: this.get('common.lunGroup_title'),
        lunGroupName: this.get('host.host_task_mapping_lunGroupName_lab'),
        portGroup: this.get('common.portGroup_title'),
        startLunId: this.get('host.host_task_mappingLun_startID_lab'),
        auto: this.get('common.EnumDefine_STARTUP_MODE_E_AUTO'),
        manu: this.get('common.EnumDefine_STARTUP_MODE_E_MANUAL'),

        mappingLunTip: this.get('host.host_task_mappingLun_selectLUN_lab'),

        unmap: this.get('host.hostGroup_task_cancleMap_lab'),
        unmapTip: this.get('host.hostGroup_task_cancleMap_selectObject_lab'),

        addHost: this.get('host.hostGroup_task_add_host_button'),
        addHostTip: this.get('host.hostGroup_task_add_host_selecthost_lab'),
        unaddHostTip: this.get('host.hostGroup_task_add_host_viewUnAddHost_lab'),
        associateWithHost: this.get('host.host_term_associateWithHost_column'),
        associateWithHostName: this.get('host.host_term_associatedHostName_column'),
        associateWithHostIpAddress: this.get('host.host_term_associatedHostIP_column'),
        removeHost: this.get('host.hostGroup_task_remove_host_button'),
        removeHostTip: this.get('host.hostGroup_task_remove_host_selecthost_lab'),

        editName: this.get('host.hostGroup_task_editName_title'),
        modifyHostGroup: this.get('host.hostGroup_task_modify_title'),
        hostGroupName: this.get('host.hostGroup_task_editName_hostGroupName_lab'),

        addNewInitiatorMenuBtn: this.get('host.host_simplifiedMode_addNewInitiator_btn'),
        addExistInitiatorMenuBtn: this.get('host.host_simplifiedMode_addExistInitiator_btn'),
        addInitiator: this.get('host.host_task_add_initiator_button'),
        addInitiatorTip: this.get('host.host_task_add_initiator_select_lab'),
        onlyOneInitiatorInfo: this.get('home.host_term_onlyOneInitiator_info'),

        removeInitiator: this.get('host.host_task_remove_initiator_button'),
        removeInitiatorTip: this.get('host.host_task_remove_initiator_select_lab'),

        modifyHost: this.get('host.host_task_modify_host_tittle'),
        modifyInitiator: this.get('host.host_task_modify_initiator_title'),
        oldPassword: this.get('common.oldPassword_label'),
        newPassword: this.get('common.newPassword_label'),

        pathDetails: this.get('host.host_view_toplogy_pathDetails_lab'),
        linkStatus: this.get('host.host_view_toplogy_pathDetails_linkStatus_lab'),
        targetPortLocation: this.get('host.host_view_toplogy_pathDetails_targetPortLocation_lab'),
        initiatorAlias: this.get('host.host_view_toplogy_pathDetails_initiatorAlias_lab'),
        viewLunId: this.get('host.host_view_toplogy_viewHostLunID_button'),
        lun: this.get('common.lun_title'),
        luns: this.get('common.objectType_LUN'),
        lunname: this.get('common.LUNName_column'),
        hostLunId: this.get('host.host_task_mappingLun_hostLunID_lab'),
        mappingStaus: this.get('common.mapping_column'),
        shared: this.get('common.EnumDefine_MOTYPE_SHARE'),
        modifyLunId: this.get('host.host_view_toplogy_modifyLunID_title'),

        initiatorAuthTargetTip: this.get('host.host_task_create_initiator_initiatorTarget_label'),
        targetAuthInitiatorTip: this.get('host.host_task_create_initiator_targetAuthInitiator_label'),

        deleteHostGroup: this.get('host.hostGroup_task_delete_title'),
        deleteHost: this.get('host.host_task_delete_title'),
        deleteInitiator: this.get('host.host_task_delete_initiator_title'),


        addInitiatorToHost: this.get('host.host_task_add_initiator_result_toHost_lab'),
        addHostToGroup: this.get('host.hostGroup_task_add_initiator_result_toGroup_lab'),

        capacityLab: this.get('common.capacity_total_column'),
        totalCapacityLab: this.get('common.totalCapacity_column'),
        alloccCapacityLab: this.get('common.allocatedCapacity_short_lab'),
        replicationCapacityLab: this.get('storage.storagePool_term_dataProtection_column'),

        modifyHostOsTitle: this.get('host.host_view_task_modifyOS_info_title'),
        healthStatus: this.get('common.healthStatus_column'),
        initiatorType: this.get('host.host_view_toplogy_pathDetails_initiatorType_cloum'),
        initiatorWWN: this.get('host.host_view_toplogy_pathDetails_initiatorIdentity_cloum'),
        targetWWN: this.get('host.host_view_toplogy_pathDetails_targetIdentity_cloum'),
        port: this.get('common.port'),
        configToPortGroup: this.get('host.host_view_toplogy_pathDetails_belongPortGroup_lab'),
        addPortGroupBtn: this.get('storage.LunGroup_view_addPortGroup_button'),
        removePortGroupBtn: this.get('storage.LunGroup_view_removePortGroup_button'),
        removePortGroupTipsForHost: this.get('storage.lunGroup_task_modifyMap_toHost_HostConGuide_msg'),
        removePortGroupTipsForHostGroup: this.get('storage.lunGroup_task_modifyMap_toHostGroup_HostConGuide_msg'),
        modifyMapBetweenLunHostPortG: this.get('storage.lun_task_modifyMap_toHost_HostConGuide_msg'),

        hostAccessMode: this.get('protection.protectionGroup_task_createHyper_hostAccessMode_label'),
        hostAccessModeTip: this.get('protection.protectionGroup_task_createHyper_hostAccessMode_tip'),
        preferredPath: this.get('protection.protectionGroup_task_createHyper_HyperPrePath_label'),
        preferredPathTip: this.get('protection.protectionGroup_task_createHyper_HyperPrePath_tip'),
        balance: this.get('protection.protectionGroup_task_createHyper_hostAccessMode_balance'),
        asymmetry: this.get('protection.protectionGroup_task_createHyper_hostAccessMode_asymmetric'),

        availableHosts: this.get('common.available', [this.get('common.Hosts')]),
        selectedHosts: this.get('common.selected', [this.get('common.Hosts')]),
        availableInitiators: this.get('common.available', [this.get('host.host_view_initiator_title')]),
        selectedInitiators: this.get('common.selected', [this.get('host.host_view_initiator_title')]),
        selectOneObjectErr: this.get('common.selectOneObject_lab'),
        hostLunIdRange: (maxHostLunId: number): string => this.i18n.get('common.valueRange_label', [0, maxHostLunId]),
        enableLab: this.get('common.enable_label'),
        fourceHostLunIDSameLab: this.get('host.host_task_mappingLun_fourceHostLunIDSame_lab'),
        fourceHostLunIDSametTip: this.get('host.host_task_mappingLun_fourceHostLunIDSame_tip'),
        fourceHostLunIDSameModifyTip: this.get('host.host_task_mappingLun_fourceHostLunIDSame_modifyID_tip'),
        advancedLab: this.get('common.advance_label'),
        advancedSettingTitle: this.get('service.share_simplifiedMode_create_advance_btn'),
        initiatorNum: this.get('host.host_term_initiatorQuantity_lab'),
        associateHostNotSame: this.get('host.initiator_task_associateHost_error_info'),
        createHostGuide: this.get('home.host_create_desc_tip'),
        initiatorCreateGuide: this.get('home.host_addInitiator_desc_tip'),
        mapToLunGuide: this.get('home.host_mapToLUN_desc_tip'),
        hostDetailTitle: this.get('home.host_detail_title'),
        hostGroupDetailTitle: this.get('home.hostGroup_detail_title'),
        confirmContent: this.get('common.confirmation_checkbox'),

        id: this.get('common.ID_column'),
        // 后缀编号
        suffixNumLab: this.get('storage.lun_task_create_suffixNumber_lab'),
        // 编号位数
        digitsLab: this.get('storage.lun_task_create_numberCharacter_lab'),
        // 到
        rangeLab: this.get('common.range'),

        wwpnOrIqnOrNqnFn: (type: number): string => {
            if (type === MO.FC_TYPE) {
                return this.i18nLabel.wwpn;
            }
            if (type === MO.ISCSI_TYPE) {
                return this.i18nLabel.iqn;
            }
            if (type === MO.NVMeoF_RoCE_TYPE) {
                return this.i18nLabel.nqn;
            }
            return this.i18nLabel.wwpn;
        },
    };

    promptTip = this.getI18n({
        createHost: 'host.host_task_create_host_msg',
        deleteHost: 'host.host_task_delete_host_msg',
        deleteHostErr: 'host.host_task_delete_host_err_msg',
        removeHost: 'host.host_task_remove_hostInHostGroup_msg',
        deleteHostGroup: 'host.hostGroup_task_delete_msg',
        deleteHostGroupErr: 'host.hostGroup_task_delete_err_msg',
        // 二级存储，计算型存储，微存储用以前的key
        addInitiator: FeatureConfig.IsShowOldConfirmTipsWhenAddInitiator ?
            'host.host_task_add_initiator_confim_msg' : 'host.host_task_add_initiator_dorado_confim_msg',
        removeInitiator: 'host.host_task_remove_initiator_msg',
        deleteInitiator: 'host.host_task_delete_initiator_msg',
        scanHostTip: 'host.host_task_scanHosts_explanation_label',
        scanHostSuccess: 'host.host_task_scanHosts_result_label',
        scanHostSuccessTip: 'host.host_task_scanHosts_result_description_label',
        scanHostFailed: 'host.host_task_scanHosts_error_message',
        scanHostFailedTip: 'host.host_task_scanHosts_fail_reason_message',
        modifyHostOsTip: 'host.host_view_task_modifyOS_info_message',
        modifyHostLunId: 'storage.lun_task_modifyHostLunId_HostConGuide_msg',
    });

    restTip = {
        createHost: (hostName: string): string => this.i18n.get('rest.createHost_msg', [hostName]),
        deleteHost: (hostName: string): string => this.i18n.get('rest.deleteHost_msg', [hostName]),
        modifyHost: (hostName: string): string => this.i18n.get('rest.modifyHost_msg', [hostName]),
        modifyHostVstore: (hostName: string): string => this.i18n.get('rest.modifyHostVstore_msg', [hostName]),
        createHostGroup: (groupName: string): string => this.i18n.get('rest.createHostGroup_msg', [groupName]),
        modifyHostGroup: (groupName: string): string => this.i18n.get('rest.modifyHostGroup_msg', [groupName]),
        deleteHostGroup: (groupName: string): string => this.i18n.get('rest.deleteHostGroup_msg', [groupName]),
        addHostToGroup: (hostName: string): string => this.i18n.get('rest.addHost_msg', [hostName]),
        removeHostFromGroup: (hostName: string): string => this.i18n.get('rest.removeHost_msg', [hostName]),
        createFCInitiator: (InitiatorName: string): string => this.i18n.get('rest.createFC_initiator_msg', [InitiatorName]),
        createISCSIInitiator: (InitiatorName: string): string => this.i18n.get('rest.createiSCSI_initiator_msg', [InitiatorName]),
        createNvmeOfRoceInitiator: (InitiatorName: string): string => this.i18n.get('rest.createNvmeofRoce_initiator_msg', [InitiatorName]),
        addInitiator: (InitiatorName: string): string => this.i18n.get('rest.addInitiator_msg', [InitiatorName]),
        getInitiatorExist: (InitiatorName: string): string => this.i18n.get('rest.queryInitiatiorExit_msg', [InitiatorName]),
        configInitiator: (InitiatorName: string): string => this.i18n.get('rest.configInitiatiorExit_msg', [InitiatorName]),
        removeInitiator: (InitiatorName: string): string => this.i18n.get('rest.removeInitiator_msg', [InitiatorName]),
        removePortGroup: (portName: string): string => this.i18n.get('rest.remove_portGroup_msg', [portName]),
        createInitiator: (type: number, InitiatorName: string): string => {
            if (type === MO.FC_TYPE) {
                return this.i18n.get('rest.createFC_initiator_msg', [InitiatorName]);
            }
            if (type === MO.ISCSI_TYPE) {
                return this.i18n.get('rest.createiSCSI_initiator_msg', [InitiatorName]);
            }
            if (type === MO.NVMeoF_RoCE_TYPE) {
                return this.i18n.get('rest.createNvmeofRoce_initiator_msg', [InitiatorName]);
            }
            return '';
        },
        modifyInitiator: (initiator: Initiator): string => {
            if (initiator.TYPE === MO.FC_TYPE) {
                return this.i18n.get('rest.modify_FCInitiator_msg', [initiator.ID]);
            }
            if (initiator.TYPE === MO.ISCSI_TYPE) {
                return this.i18n.get('rest.modify_iSCSIInitiator_msg', [this.utilService.decodeHTML(initiator.ID)]);
            }
            if (initiator.TYPE === MO.NVMeoF_RoCE_TYPE) {
                return this.i18n.get('rest.modify_nvmeofRoceInitiator_msg', [this.utilService.decodeHTML(initiator.ID)]);
            }
            return '';
        },
        modifyInitiatorVstore: (initiator: Initiator): string => {
            if (initiator.TYPE === MO.FC_TYPE) {
                return this.i18n.get('rest.modify_FCInitiatorVstore_msg', [initiator.ID]);
            }
            if (initiator.TYPE === MO.ISCSI_TYPE) {
                return this.i18n.get('rest.modify_iSCSIInitiatorVstore_msg', [this.utilService.decodeHTML(initiator.ID)]);
            }
            if (initiator.TYPE === MO.NVMeoF_RoCE_TYPE) {
                return this.i18n.get('rest.modify_nvmeofRoceInitiatorVstore_msg', [this.utilService.decodeHTML(initiator.ID)]);
            }
            return '';
        },
        deleteInitiator: (initiator: Initiator): string => {
            if (initiator.TYPE === MO.FC_TYPE) {
                return this.i18n.get('rest.delete_FCInitiator_msg', [initiator.ID]);
            }
            if (initiator.TYPE === MO.ISCSI_TYPE) {
                return this.i18n.get('rest.delete_iSCSIInitiator_msg', [this.utilService.decodeHTML(initiator.ID)]);
            }
            if (initiator.TYPE === MO.NVMeoF_RoCE_TYPE) {
                return this.i18n.get('rest.delete_nvmeofRoceInitiator_msg', [this.utilService.decodeHTML(initiator.ID)]);
            }
            return '';
        },
    };

    errorMsg = {
        wwpn: this.i18n.get('host.host_task_create_initiator_FC_WWPN_tip'),
        range: this.i18n.get('host.host_task_batchCreate_quantity_tip'),
        start: this.i18n.get('common.modifyPassword_tip'),
        ascii: this.i18n.get('host.host_task_create_initiator_iSCSI_IQN_tip'),
        chapName: this.i18n.get('host.host_task_create_initiator_iSCSI_chapName_tip'),
        chapPassword: this.i18n.get('host.host_task_create_initiator_iSCSI_chapPassword_tip'),
        equalReverse: this.i18n.get('host.host_task_create_initiator_iSCSI_equalReversePassword_tip'),
        notEqual: this.i18n.get('common.pwdNotEqual_tip'),
        equal: this.i18n.get('common.oldpwdNoteqltoNew_tip'),
    };

    topoViewTypeText = {
        topo: this.i18n.get('host.hostGroup_view_topology_tableView_lab'),
        table: this.i18n.get('common.topology_lab'),
    };

    ipFormatErrMsg = this.i18n.get('common.IPAddr_validation');

    osOBj = this.getI18n({
        0: 'common.EnumDefine_OS_TYPE_E_LINUX',
        1: 'common.EnumDefine_OS_TYPE_E_WINDOWS',
        2: 'common.EnumDefine_OS_TYPE_E_SOLARIS',
        3: 'common.EnumDefine_OS_TYPE_E_HPUX',
        4: 'common.EnumDefine_OS_TYPE_E_AIX',
        5: 'common.EnumDefine_OS_TYPE_E_XenServer',
        6: 'common.EnumDefine_OS_TYPE_E_MACOS',
        7: 'common.EnumDefine_OS_TYPE_E_ESX',
        8: 'common.EnumDefine_OS_TYPE_E_LINUXVIS',
        9: 'common.EnumDefine_OS_TYPE_E_WINDOWS',
        10: 'common.EnumDefine_OS_TYPE_E_Oracle_VM_Server_for_x86',
        11: 'common.EnumDefine_OS_TYPE_E_OpenVMS',
        12: 'common.EnumDefine_OS_TYPE_E_Oracle_VM_Server_for_x86',
        13: 'common.EnumDefine_OS_TYPE_E_Oracle_VM_Server_for_SPARC',
    });

    /**
     * DM界面不支持创建windows server2012以及Oracle_VM操作系统的主机，并将CLI创建的该类型主机分别显示为
     * Windows以及Oracle_VM_Server_for_x86
     */
    osObjFilterParams = {
        OS_TYPE_E_WINDOWS: {
            displayValue: HostOsTypeE.WINDOWS,
            hiddenValue: HostOsTypeE.WINDOWS_SERVER_2012,
        },
        OS_TYPE_E_ORACLE_VM_SEVER_FOR_X86: {
            displayValue: HostOsTypeE.ORACLE_VM_SERVER_FOR_X86,
            hiddenValue: HostOsTypeE.ORACLE_VM,
        },
    };

    pathTypeObj = {
        0: this.i18n.get('host.host_task_create_configALUA_pathType_optimalRoute_label'),
        1: this.i18n.get('host.host_task_create_configALUA_pathType_noOptimalPath_label'),
    };

    authObj = {
        0: this.i18nLabel.noAuth,
        1: this.i18nLabel.unidicectional,
        2: this.i18nLabel.bidirectional,
    };

    mappingType = this.getI18n({
        0: 'common.mapping_unMapped_value',
        1: 'common.host_title',
        2: 'common.hostGroup_list_title',
        3: 'common.lun_title',
        4: 'common.lunGroup_title',
    });

    isFreeObj = this.getI18n({
        true: 'common.term_No_label',
        false: 'common.term_yes_label',
    });

    configToPortGroupObj = {
        true: this.get('common.term_yes_label'),
        false: this.get('common.term_No_label'),
    };

    noRedundancyCheckObj = {
        [HostNoRedundancyCheckE.DISABLE]: this.i18nLabel.noLab,
        [HostNoRedundancyCheckE.ENABLE]: this.i18nLabel.yesLab,
    };

    healthStatusObj = this.getI18n({
        1: 'common.EnumDefine_STATUS_E_NORMAL',
        2: 'common.EnumDefine_HEALTH_STATUS_fault',
        17: 'common.EnumDefine_HEALTH_STATUS_E_NO_REDUNDANT_LINK',
        18: 'common.EnumDefine_RUNNING_STATUS_E_OFFLINE',
    });

    // 健康状态图标
    healthStatusSvg = {
        1: 'normal',
        2: 'faulty',
        17: 'write-protected',
        18: 'write-protected',
    };

    runningStatusObj = this.getI18n({
        10: 'common.EnumDefine_RUNNING_STATUS_E_LINK_UP',
        11: 'common.EnumDefine_RUNNING_STATUS_E_LINK_DOWN',
        27: 'common.EnumDefine_RUNNING_STATUS_E_ONLINE',
        31: 'common.EnumDefine_RUNNING_STATUS_E_DISABLED',
        101: 'common.EnumDefine_RUNNING_STATUS_E_CONNECTING',
    });

    aluaProtocolObj = this.getI18n({
        0: 'protection.protectionGroup_task_createHyper_hostAccessMode_balance',
        1: 'protection.protectionGroup_task_createHyper_hostAccessMode_asymmetric',
    });

    pathPreferredObj = this.getI18n({
        1: 'common.term_yes_label',
        0: 'common.term_No_label',
    });

    initiatorIdLabel = {
        223: this.i18nLabel.wwpn,
        222: this.i18nLabel.iqn,
        57870: this.i18nLabel.nqn,
    };

    initiatorTypeObj = this.getI18n({
        223: 'common.FC_lab',
        222: 'common.iSCSI_lab',
        16499: 'IB',
        57870: 'common.NVMeoF_RoCE_lab',
    });

    initiatorTypeRestMo = {
        223: 'fcInitiator',
        222: 'iscsiInitiator',
        57870: 'nvmeofRoceInitiator',
    };

    initiatorTypeOptions = this.getOptionByType();

    tableColumnDefs = [
        {
            field: 'TYPE',
            header: this.i18nLabel.type,
            columnFilter: true,
            width: '1.5rem',
            render: (value: string): string => this.initiatorTypeObj[value] || UNPLACEHOLDER,
        },
        {
            field: 'ID',
            header: (FeatureConfig.IsInitiatorIdToBeIqn) ? this.i18nLabel.iqn : this.i18nLabel.wwpnOrIqnOrNqn,
            render: (value: string): string => this.utilService.decodeHTML(value),
        },
        {
            field: 'NAME',
            header: this.i18nLabel.alias,
            render: (value: string): string => value || UNPLACEHOLDER,
        },
    ];

    runningStatusColumn: ColumnDef = {
        field: 'RUNNINGSTATUS',
        filterType: 'dropdown',
        header: this.i18nLabel.status,
        options: [
            {
                value: '27',
                label: this.i18nLabel.online,
            },
            {
                value: '28',
                label: this.i18nLabel.offline,
            },
            {
                value: '0',
                label: this.i18nLabel.unknown,
            },
        ],
        render: (data, col) => this.utilService.getLabelFromOptions(col.options as SelectOption[], data),
        width: '70px',
        columnFilter: true,
    };

    healthStatusOpts = this.obj2Options(this.healthStatusObj);

    osOptions = this.obj2Options(this.osOBj)
        .filter((item) => {
            // 微存储支持的主机类型从配置文件获取
            if (ProductConfig.SupportHostOsListTypeE) {
                const supportArr = ProductConfig.SupportHostOsListTypeE.split(',');
                return supportArr.includes(item.value);
            }
            // 用于选项时过滤掉 Windows_Server_2012和Oracle_VM的枚举值
            return item.value !== '9' && item.value !== '10';
        });

    aluaProtocolOpts: SelectOption[] = this.obj2Options(this.aluaProtocolObj);

    isFreeOpts: SelectOption[] = [
        {
            label: this.get('common.term_yes_label'),
            value: 'false',
        },
        {
            label: this.get('common.term_No_label'),
            value: 'true',
        },
    ];

    pathPreferredOpts: SelectOption[] = [
        {
            label: this.get('common.term_yes_label'),
            value: '1',
        },
        {
            label: this.get('common.term_No_label'),
            value: '0',
        },
    ];

    authOptions = this.obj2Options(this.authObj);

    // 自定义编号位数
    digitsOpt = [
        {
            label: '1',
            value: 1,
        },
        {
            label: '2',
            value: 2,
        },
        {
            label: '3',
            value: 3,
        },
        {
            label: '4',
            value: 4,
        },
    ];

    constructor(
        public i18n: I18nService,
        private utilService: UtilService,
        private permissionService: PermissionService,
    ) { }

    /**
     * 获取操作系统过滤参数(由于DM界面将CLI创建的操作系统枚举值为9以及10的主机类型显示为枚举值为1以及12的类型，
     * 故应当在过滤操作系统类型为1的主机同时过滤出类型为9的，过滤类型为12的主机同时过滤出类型为10的)
     */
    getOsObjFiltersBeforeQuery = (params: Params): Params => {
        if (!Array.isArray(params.filters)) {
            return params;
        }

        const filters = params.filters as Filter[];
        params.filters = [[...filters]];
        const osIndex = filters.findIndex((item: Filter) => item.field === 'OPERATIONSYSTEM');

        if (osIndex === -1) {
            return params;
        }

        // 1下发过滤1 or 9
        if (filters[osIndex].value === this.osObjFilterParams.OS_TYPE_E_WINDOWS.displayValue) {
            filters[osIndex] = {
                field: 'OPERATIONSYSTEM',
                matchMode: '::',
                value: this.osObjFilterParams.OS_TYPE_E_WINDOWS.hiddenValue,
            };
            params.filters.push([...filters]);
        }

        // 12下发过滤10 or 12
        if (filters[osIndex].value === this.osObjFilterParams.OS_TYPE_E_ORACLE_VM_SEVER_FOR_X86.displayValue) {
            filters[osIndex] = {
                field: 'OPERATIONSYSTEM',
                matchMode: '::',
                value: this.osObjFilterParams.OS_TYPE_E_ORACLE_VM_SEVER_FOR_X86.hiddenValue,
            };
            params.filters.push([...filters]);
        }

        return params;
    };

    getOptionByType(): SelectOption[] {
        let options = [
            {
                value: MO.FC_TYPE,
                label: this.i18nLabel.FCLab,
            },
            {
                value: MO.ISCSI_TYPE,
                label: this.i18nLabel.iSCSILab,
            },
            {
                value: MO.NVMeoF_RoCE_TYPE,
                label: this.i18nLabel.nvmeofRoceLab,
            },
        ];

        /**
         * 计算型存储屏蔽FC类型和ROCE类型
         * 入门级存储屏蔽ROCE类型
         * 微存储直通不支持fc启动器和iscsi启动器
         */
        if (!FeatureConfig.IsSupportFC) {
            options = options.filter((item) => item.value !== MO.FC_TYPE);
        }

        if (!FeatureConfig.IsSupportNVMEOverRoCEInitiator) {
            options = options.filter((item) => item.value !== MO.NVMeoF_RoCE_TYPE);
        }

        if (!FeatureConfig.IsSupportISCSI) {
            options = options.filter((item) => item.value !== MO.ISCSI_TYPE);
        }

        return options;
    }

    modifyHostLunId(lunName: string): string {
        return this.i18n.get('rest.modify_hostLunID_msg', [lunName]);
    }

    modifyHost(hostName: string): string {
        return this.i18n.get('rest.createHost_msg', [hostName]);
    }

    nullErrMsg(name: string): string {
        const i18nName = this.i18nLabel[name];
        return this.i18n.get('common.null_validation', [i18nName]);
    }

    nameErrMsg(name: string): string {
        const i18nName = this.i18nLabel[name];
        return this.i18n.get('common.nameformat_validation', [i18nName]);
    }

    lengthErrMsg(min: number, max: number): string {
        return this.i18n.get('common.length_msg', [min, max]);
    }

    repeatErrMag(name: string): string {
        const i18nName = this.i18nLabel[name];
        return this.i18n.get('common.allCanNotEquall_msg', [i18nName]);
    }

    usageStr = (...args: number[]): string => this.i18n.get('common.usedandTotal_tip', args);

    rangeStr = (from: number, to: number): string => this.i18n.get('common.range_vaild', [from, to]);

    obj2Options(obj: { [key: string]: string }): SelectOption[] {
        return Object.keys(obj).map((key) => {
            const option = {
                value: key,
                label: obj[key],
            };
            return option;
        });
    }

    getI18n(kv: { [key: string]: string }): { [key: string]: string } {
        const keys = Object.keys(kv);
        const result = {};
        keys.forEach((key) => {
            result[key] = this.i18n.get(kv[key]);
        });
        return result;
    }

    get(key: string, params?: any[] | boolean, colon?: boolean): string {
        return this.i18n.get(key, params, colon);
    }

    /**
     * 已分配容量渲染
     */
    usedCapacityRender = (usedCapacity: string, totalCapacity: string): string => this.get('common.allocatedandTotal_tip', [
        this.utilService.adaptUnit(usedCapacity, CAPACITY_UNIT.SECTOR).label,
        this.utilService.adaptUnit(totalCapacity, CAPACITY_UNIT.SECTOR).label,
    ]);

    /**
     * 未使用容量渲染
     */
    freeCapacityRender = (oriFreeCapacity: string | number): string => {
        let freeCapacity = oriFreeCapacity;
        freeCapacity = Math.max(0, Number(freeCapacity));
        return this.get('common.unused_tip', [
            this.utilService.adaptUnit(freeCapacity, CAPACITY_UNIT.SECTOR).label,
        ]);
    };

    /**
     * 固定长度错误提示
     */
    fixLengthErrMsg(length: number | string): string {
        const lengthStr = String(length);
        return this.get('common.length_character_valid', [lengthStr]);
    }

    /**
     * 判断是否支持主机链路冗余性检查
     */
    isSupportNoRedundancyCheck(): boolean {
        return this.permissionService.isLicenseExist([[LicenseKey.VirtualEnvironmentEnabler]]);
    }
}
